import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { getUserId, getUserName } from '@redux/selectors/UserSelector';
import { getImpersonationStatus } from '@redux/selectors/ImpersonationSelector';

import { isEmpty, isNull } from 'lodash';

import AgentAvatar from '@components/table/avatar/AgentAvatar';
import TableLoaderSmall from '@components/loaders/TableLoaderSmall';
import ListingTypeIcons from '@components/table/icons/ListingTypeIcons';
import ListingActionButtons from '@components/table/actionButtons/ListingActionButtons';
import TableSearchInputGroup from '@components/smartAdminUI/input/TableSearchInputGroup';
import StatusBadges from '@components/badges/StatusBadges';
import DataTable from '../dataTable';
import translateColumns from '../dataTable/dataTranslate';

const ListingsTableWidget = ({
  displayData,
  onListingSelect,
}) => {
  const [slicedTableData, setSlicedTableData] = useState(displayData);
  const realUserName = useSelector(getUserName);
  const realUserId = useSelector(getUserId);
  const impersonationStatus = useSelector(getImpersonationStatus);
  const listingsLoading = useSelector(state => state.listing.editableListingLoading);
  const showBrokerListings = useSelector(state => state.listing.showBrokerListings);
  const hideAgentFullName = useSelector(state => !state.listing.showBrokerListings);
  const currentUser = useMemo(() => (impersonationStatus?.ImpersonationActive
    ? { userName: impersonationStatus.memberFullName, userId: impersonationStatus.memberUniqueID }
    : { userName: realUserName, userId: realUserId }
  ), [realUserName, realUserId, impersonationStatus]);

  const actionsFormatter = (cell, row) => (
    <ListingActionButtons
      viewClicked={onListingSelect}
      copyClicked={() => {}}
      auditClicked={() => {}}
      currRow={row}
      isCopiable={false}
      isEditable={row?.isEditable}
      isAuditable={false}
      sizeSM
    />
  );

  const hdrSortClasses = (_column, sortOrder) => `sorting_${sortOrder}`;

  const filterByAgentKeys = (filterVal, data) => {
    const filteredData = data.filter(listing => {
      const listAgentKey = isNull(listing.ListAgentKeyNumeric) ? '' : listing.ListAgentKeyNumeric.toString();
      const coListAgentKey = isNull(listing.CoListAgentKeyNumeric) ? '' : listing.CoListAgentKeyNumeric.toString();
      return isNull(filterVal) || filterVal === ''
        ? true
        : listAgentKey.includes(filterVal) || coListAgentKey.includes(filterVal);
    });
    setSlicedTableData(filteredData.slice(0, 500));

    return filteredData;
  };

  useEffect(() => {
    if (isNull(currentUser.userId)) return;
    const userId = isNull(currentUser.userId) ? '' : currentUser.userId.toString();
    filterByAgentKeys(showBrokerListings ? null : userId, displayData);
  }, [currentUser, showBrokerListings, displayData]);

  const columns = useMemo(() => [
    {
      dataField: 'actions',
      text: ' ',
      formatter: actionsFormatter,
      headerStyle: { width: '30px' },
    },
    {
      dataField: 'UniqueID',
      hidden: true,
      sort: false,
      searchable: false,
    },
    {
      dataField: 'ListingId',
      text: 'ID',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '40px' },
    },
    {
      dataField: 'StandardStatusVerb',
      text: 'Status',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      align: 'center',
      headerStyle: { width: '50px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <StatusBadges status={cellContent} />),
    },
    {
      dataField: 'PropertyType',
      text: 'Type',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      align: 'center',
      headerStyle: { width: '40px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <ListingTypeIcons type={cellContent} />),
    },
    {
      dataField: 'UnparsedAddress',
      text: 'Address',
      title: true,
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      classes: 'text-ellipsis-175',
    },
    {
      dataField: 'City',
      text: 'City',
      title: true,
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      classes: 'text-ellipsis-100',
    },
    {
      dataField: 'ListAgentFullName',
      text: 'Agent',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      hidden: hideAgentFullName,
      sort: true,
      rows: '1',
      align: 'center',
      headerStyle: { width: '40px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <AgentAvatar fullName={cellContent} showFullName={false} />),
    },
    {
      dataField: 'ListAgentKeyNumeric',
      text: 'keys',
      style: { display: 'none' },
      className: 'd-none',
      headerStyle: { display: 'none' },
      sort: false,
      searchable: false,
    },
    {
      dataField: 'ModifiedDate',
      text: 'ModifiedDate',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: false,
      hidden: true,
      searchable: false,
    },
  ], [hideAgentFullName]);

  const newCols = useMemo(() => translateColumns(columns), [columns]);

  // Sets the default sort order to desc by ListingId
  const defaultSorted = [{
    id: 'ListingId',
    desc: true,
  }];

  return (
    <>
      <div className={listingsLoading ? '' : 'd-none'}>
        <TableLoaderSmall />
      </div>
      <div className={listingsLoading ? 'd-none' : ''}>
        <DataTable
          defaultSorted={defaultSorted}
          columns={newCols}
          sizePerPage={5}
          data={slicedTableData}
          wrapperClasses="table-responsive"
          id="listingTable"
          noDataIndication="No Listing Data."
          classes="dataTable"
          striped
          hover
          searchComponent={searchProps => <TableSearchInputGroup {...searchProps} />}
        />
      </div>
    </>
  );
};

ListingsTableWidget.propTypes = {
  displayData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onListingSelect: PropTypes.func.isRequired,
  onColumnToggle: PropTypes.func,
  isModal: PropTypes.bool,
};

export default ListingsTableWidget;
